import React, { createContext, useEffect, useMemo, useState } from 'react'
import { Trans } from '@lingui/macro'
import { Link } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import queryString from 'query-string'

import IconBan from '../../../components/common/icon/IconBan'
import IconCheck from '../../../components/common/icon/IconCheck'
import Spinner from '../../../components/common/loader/Loader'
import StudentSurveyQuestion from './StudentSurveyQuestion'
import { linkBuilder, staticLinks } from 'shared/linkBuilder'

import { getScheduledActionIdFromURL } from '../../../utils/campaignTracking'
import {
  SurveyQuery,
  useSurveyOpenedFromCampaignShareEmailMutation,
  useSurveyQuery
} from './StudentSurvey.codegen'
import { ISurveyQuestion } from '../types'

import './StudentSurvey.less'

export const getLatestAnsweredQuestionIndex = (
  questions: ISurveyQuestion[]
) => {
  return questions.findIndex((question) => question.userAnswer === null)
}

export const StudentSurveyContext = createContext<{
  survey: NonNullable<SurveyQuery['survey']>
  refetchSurvey: () => void
}>(null as any)

export const StudentSurveyPage = ({ match, location, history }) => {
  const isPreviewMode = () => match.path.includes('preview')
  const [activeQuestion, setActiveQuestion] = useState<number>(0)
  const [prevQuestion, setPrevQuestion] = useState<number>(0)
  const [questions, setQuestions] = useState<ISurveyQuestion[]>([])
  const [trackOpenedFromCampaign] =
    useSurveyOpenedFromCampaignShareEmailMutation()

  useEffect(() => {
    // Campaigns links click tracking
    const parsedQueryString = queryString.parse(location.search)
    const scheduledActionId = getScheduledActionIdFromURL(parsedQueryString)

    if (scheduledActionId && !isPreviewMode()) {
      // This survey was opened from Survey share action email sent from campaign.
      trackOpenedFromCampaign({
        variables: {
          isPreviewMode: false,
          id: match.params.surveyId,
          scheduledActionId: scheduledActionId
        }
      })

      // Remove scheduled_action param from current history state (means from current URL in browser)
      delete parsedQueryString.scheduled_action
      history.replace(
        `${linkBuilder.surveyUrl(
          parseInt(match.params.surveyId, 10)
        )}?${queryString.stringify(parsedQueryString)}`
      )
    }
  }, [match.params.surveyId])

  const {
    data,
    refetch: refetchSurvey,
    loading
  } = useSurveyQuery({
    variables: {
      id: parseInt(match.params.surveyId),
      isPreviewMode: isPreviewMode()
    }
  })

  useMemo(() => {
    if (data?.survey?.questions) {
      setQuestions(data.survey.questions)
      if (!isPreviewMode()) {
        setActiveQuestion(getLatestAnsweredQuestionIndex(data.survey.questions))
      }
    }
  }, [data])

  if (loading) {
    return <Spinner />
  }

  if (!data?.survey) {
    return (
      <div className="StudentSurvey__info">
        <IconBan />
        <h2>
          <Trans>
            You don't have permissions to see this survey or this survey does
            not exist
          </Trans>
        </h2>
        <hr />
        <Trans>
          Go back <Link to={staticLinks.home}>home</Link>
        </Trans>
      </div>
    )
  }

  if (data.survey.completed_at && !isPreviewMode()) {
    return (
      <div className="StudentSurvey__info">
        <IconBan />
        <h2>
          <Trans>Survey is closed</Trans>
        </h2>
        <hr />
        <Trans>
          Go back <Link to={staticLinks.home}>home</Link>
        </Trans>
      </div>
    )
  }

  if (activeQuestion === -1 && !isPreviewMode()) {
    return (
      <div className="StudentSurvey__info">
        <IconCheck />
        <h2>
          <Trans>You've already completed this survey</Trans>
        </h2>
        <hr />
        <Trans>
          Go back <Link to={staticLinks.home}>home</Link>
        </Trans>
      </div>
    )
  }

  const page = (
    <motion.div
      key={activeQuestion}
      initial={{ x: 500, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{
        x: { type: 'tween', easy: 'easeOut', duration: 0.4 },
        opacity: { duration: 0.3 }
      }}
      style={{
        position: 'absolute',
        width: '100%'
      }}
    >
      <StudentSurveyQuestion
        questions={questions}
        setQuestions={setQuestions}
        question={questions[activeQuestion]}
        indexOfPage={activeQuestion}
        countOfPages={questions.length}
        setActivePage={setActiveQuestion}
        isPreviewMode={isPreviewMode()}
      />
    </motion.div>
  )

  if (activeQuestion !== prevQuestion) setPrevQuestion(activeQuestion)

  return (
    <StudentSurveyContext.Provider
      value={{ survey: data.survey, refetchSurvey }}
    >
      <div className="StudentSurvey">
        {isPreviewMode() && (
          <Link
            className="StudentSurvey__back"
            to={linkBuilder.surveyDetailManage(data.survey.id, 'create')}
          >
            <span className="icon-left" /> <Trans>Exit preview</Trans>
          </Link>
        )}
        <AnimatePresence>{page}</AnimatePresence>
      </div>
    </StudentSurveyContext.Provider>
  )
}
